import { Client } from 'src/admin/users/types';
import {
  CreateFamilyPayload,
  FamilyMemberWithoutRelation,
  FamilyMemberWithRelation,
} from '../hooks/useCreateFamily';
import { Relation } from '../hooks/useFamily';
import { AddManuallyFormValues } from './AddContactsToFamily/AddManuallyView';

export type InProgressFamily = Partial<
  Omit<CreateFamilyPayload, 'secondaryFamilyMembers' | 'mainFamilyMember'>
> & {
  mainFamilyMember?: InProgressMainMember;
  secondaryFamilyMembers?: InProgressSecondaryMember[];
};
export type InProgressMainMember = Client | AddManuallyFormValues;
export type InProgressSecondaryMember = InProgressMainMember & {
  relationshipToMainFamilyMember?: Relation;
};

export function payloadFromInProgressFamily(family: InProgressFamily): CreateFamilyPayload {
  const { advisorUserId } = family;
  if (!family.name || !advisorUserId || !family.mainFamilyMember) {
    throw new Error('Missing required family data');
  }

  return {
    name: family.name,
    advisorUserId,
    mainFamilyMember: payloadWithoutRelation(family.mainFamilyMember, advisorUserId),
    secondaryFamilyMembers:
      family.secondaryFamilyMembers?.map(m => payloadWithRelation(m, advisorUserId)) || [],
  };
}

function payloadWithoutRelation(
  member: InProgressMainMember,
  advisorUserId: string,
): FamilyMemberWithoutRelation {
  if ('id' in member) {
    return { clientId: member.id } as FamilyMemberWithoutRelation;
  }

  return {
    ...member,
    advisorUserId,
  };
}

function payloadWithRelation(
  member: InProgressSecondaryMember,
  advisorUserId: string,
): FamilyMemberWithRelation {
  return {
    ...payloadWithoutRelation(member, advisorUserId),
    relationshipToMainFamilyMember: member.relationshipToMainFamilyMember || 'Other',
  };
}

type ComparableFamilyMember = InProgressMainMember | InProgressSecondaryMember;
export function isSameFamilyMember(a: ComparableFamilyMember, b: ComparableFamilyMember) {
  return memberId(a) === memberId(b);
}

export function memberId(member: ComparableFamilyMember) {
  if ('id' in member && member.id) {
    return member.id;
  }

  return [member.emailAddress, member.firstName, member.lastName].filter(Boolean).join('-');
}
